import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SettingsAdminService, WaitingListStatus, WaitingListContact, ContactsListStatusDef, AdminSettingDetail } from "../services/settingsAdminService";
import { AddContactDialog } from "./AddContactDialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/shared/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/shared/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import { Plus, Trash2, UserPlus, Search, ChevronDown, ChevronRight } from "lucide-react";
import { usePagination } from "@/shared/hooks/usePagination";
import { DataPagination } from "@/shared/components/DataPagination";

const STATUS_COLOR_MAP: Record<string, string> = {
  green: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  yellow: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  gray: "bg-muted text-muted-foreground",
  red: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  purple: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  orange: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
};

// Picks the label for the active language, falling back to English.
function localizedLabel(label: { en: string; pt?: string }, lang: string) {
  return label[lang as "en" | "pt"] ?? label.en;
}

// ── New Waitlist Dialog ──────────────────────────────────────────────────────

interface NewWaitlistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function NewWaitlistDialog({ open, onOpenChange }: NewWaitlistDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [type, setType] = useState("");
  const [labelEn, setLabelEn] = useState("");
  const [labelPt, setLabelPt] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      SettingsAdminService.createSetting({
        type,
        fields: {
          schema_version: "1.0",
          type: "contacts-list",
          label: { en: labelEn || type, pt: labelPt || type },
          description: { en: "", pt: "" },
          contacts_key: "contacts",
          statuses: [
            { value: "available",  label: { en: "Available",  pt: "Disponível" }, color: "green"  },
            { value: "redeemed",   label: { en: "Redeemed",   pt: "Resgatado"  }, color: "blue"   },
            { value: "requested",  label: { en: "Requested",  pt: "Solicitado" }, color: "yellow" },
            { value: "pending",    label: { en: "Pending",    pt: "Pendente"   }, color: "gray"   },
            { value: "revoked",    label: { en: "Revoked",    pt: "Revogado"   }, color: "red"    },
          ],
        },
        value: { contacts: [] },
        secure: 1,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
      setType("");
      setLabelEn("");
      setLabelPt("");
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("settings.waitlists.newDialog.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="wl-type">{t("settings.waitlists.newDialog.keyLabel")}</Label>
            <Input
              id="wl-type"
              value={type}
              onChange={(e) => setType(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
              placeholder={t("settings.waitlists.newDialog.keyPlaceholder")}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">{t("settings.waitlists.newDialog.keyHint")}</p>
          </div>
          <div className="space-y-1">
            <Label>{t("settings.waitlists.newDialog.label")}</Label>
            <div className="flex gap-2">
              <Input value={labelEn} onChange={(e) => setLabelEn(e.target.value)} placeholder={t("settings.waitlists.newDialog.english")} />
              <Input value={labelPt} onChange={(e) => setLabelPt(e.target.value)} placeholder={t("settings.waitlists.newDialog.portuguese")} />
            </div>
          </div>
          {mutation.isError && (
            <p className="text-xs text-destructive">{(mutation.error as Error)?.message ?? t("settings.waitlists.newDialog.createError")}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("settings.waitlists.newDialog.cancel")}</Button>
          <Button onClick={() => mutation.mutate()} disabled={!type || mutation.isPending}>
            {mutation.isPending ? t("settings.waitlists.newDialog.creating") : t("settings.waitlists.newDialog.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Contacts panel for a single waitlist ─────────────────────────────────────

interface ContactsPanelProps {
  setting: AdminSettingDetail;
}

function ContactsPanel({ setting }: ContactsPanelProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];
  const queryClient = useQueryClient();
  const listType = setting.type;
  const statuses: ContactsListStatusDef[] = (setting.fields as { statuses?: ContactsListStatusDef[] })?.statuses ?? [];
  const contacts: WaitingListContact[] = setting.contacts ?? [];
  const stats = setting.stats ?? {};

  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData, resetPagination } = usePagination<WaitingListContact>({
    initialPage: 1,
    initialPageSize: 10,
  });

  const STATUS_ORDER: Record<string, number> = {
    requested: 0,
    pending:   1,
    available: 2,
    redeemed:  3,
    revoked:   4,
    expired:   5,
  };

  const filtered = contacts
    .filter((c) => c.email.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99));

  const paginated = paginateData(filtered);

  const getColor = (statusValue: string) => {
    const def = statuses.find((s) => s.value === statusValue);
    return def ? (STATUS_COLOR_MAP[def.color] ?? STATUS_COLOR_MAP.gray) : STATUS_COLOR_MAP.gray;
  };

  const getLabel = (statusValue: string) => {
    const def = statuses.find((s) => s.value === statusValue);
    return def ? localizedLabel(def.label, lang) : statusValue;
  };

  const updateStatus = useMutation({
    mutationFn: ({ email, status }: { email: string; status: WaitingListStatus }) =>
      SettingsAdminService.updateContactStatus(listType, email, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-setting", listType] }),
  });

  const remove = useMutation({
    mutationFn: (email: string) => SettingsAdminService.removeContact(listType, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-setting", listType] });
      setDeleteTarget(null);
    },
  });

  return (
    <div className="space-y-3">
      {/* Stats */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="text-muted-foreground">{t("settings.waitlists.contacts.total")}: <strong>{stats.total ?? 0}</strong></span>
        {statuses.map((s) => (
          <span key={s.value} className={`px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR_MAP[s.color] ?? STATUS_COLOR_MAP.gray}`}>
            {localizedLabel(s.label, lang)}: {stats[s.value] ?? 0}
          </span>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("settings.waitlists.contacts.searchPlaceholder")}
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPagination(); }}
            className="pl-8 h-8"
          />
        </div>
        <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
          <UserPlus className="h-4 w-4 mr-1" /> {t("settings.waitlists.contacts.addContact")}
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("settings.waitlists.contacts.email")}</TableHead>
              <TableHead className="w-44">{t("settings.waitlists.contacts.status")}</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground py-6 text-sm">
                  {search ? t("settings.waitlists.contacts.noMatch") : t("settings.waitlists.contacts.empty")}
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((contact) => (
                <TableRow key={contact.email}>
                  <TableCell className="font-mono text-sm">{contact.email}</TableCell>
                  <TableCell>
                    <Select
                      value={contact.status}
                      onValueChange={(v) =>
                        updateStatus.mutate({ email: contact.email, status: v as WaitingListStatus })
                      }
                    >
                      <SelectTrigger className="h-7 w-36 text-xs border-none shadow-none p-1">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getColor(contact.status)}`}>
                          {getLabel(contact.status)}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLOR_MAP[s.color] ?? STATUS_COLOR_MAP.gray}`}>
                              {localizedLabel(s.label, lang)}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(contact.email)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <DataPagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={filtered.length}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[10, 20, 50]}
      />

      {addOpen && (
        <AddContactDialog open={addOpen} onOpenChange={setAddOpen} listType={listType} statuses={statuses} />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("settings.waitlists.contacts.removeTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.waitlists.contacts.removeDescription", { email: deleteTarget })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("settings.waitlists.contacts.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && remove.mutate(deleteTarget)}
            >
              {remove.isPending ? t("settings.waitlists.contacts.removing") : t("settings.waitlists.contacts.remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Waitlist card (expandable) ───────────────────────────────────────────────

interface WaitlistCardProps {
  type: string;
  label: string;
  defaultOpen?: boolean;
}

function WaitlistCard({ type, label, defaultOpen = false }: WaitlistCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultOpen);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-setting", type],
    queryFn: () => SettingsAdminService.getSetting(type),
    enabled: expanded,
  });

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none py-3 px-4"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            {label}
            <code className="text-xs font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{type}</code>
          </CardTitle>
          {data?.stats && (
            <span className="text-xs text-muted-foreground">{t("settings.waitlists.contactsCount", { count: data.stats.total ?? 0 })}</span>
          )}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : data ? (
            <ContactsPanel setting={data} />
          ) : null}
        </CardContent>
      )}
    </Card>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────────

export function WaitlistsPanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];
  const [newOpen, setNewOpen] = useState(false);

  const { data: allSettings, isLoading } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: () => SettingsAdminService.getSettings(),
  });

  const waitlists = (allSettings ?? []).filter(
    (s) => (s.fields as { type?: string })?.type === "contacts-list"
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">{t("settings.waitlists.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("settings.waitlists.description")}</p>
        </div>
        <Button size="sm" onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> {t("settings.waitlists.newWaitlist")}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
        </div>
      ) : waitlists.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            {t("settings.waitlists.empty")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {waitlists.map((s) => (
            <WaitlistCard
              key={s.type}
              type={s.type}
              label={localizedLabel((s.fields as { label?: { en: string; pt?: string } })?.label ?? { en: s.type }, lang)}
            />
          ))}
        </div>
      )}

      <NewWaitlistDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
